import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, Button, Divider, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import { ArrowLeftIcon } from '@chakra-ui/icons';
import CareersFooter from '../components/CareersComponents/CareersFooter';
import TeamActivityBreadcrumb from '../components/CareersComponents/TeamActivityComponents/TeamActivityBreadcrumb';
import TeamActivityHeroImage from '../components/CareersComponents/TeamActivityComponents/TeamActivityHeroImage';
import TeamActivityTitleSection from '../components/CareersComponents/TeamActivityComponents/TeamActivityTitleSection';
import TeamActivityContent from '../components/CareersComponents/TeamActivityComponents/TeamActivityContent';
import { useTeamActivity, useTeamActivityPost } from '../hooks/useTeamActivity';

const TeamActivityDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Use the actual ID from URL params
  const actualId = id;
  
  // Try to get team activity post from location state first
  // const initialActivityPost = location.state?.blogPost; // Note: the state key is still blogPost for compatibility
  const currentPage = location.state?.currentPage || 1;
  
  // Fetch single team activity post from API using the new hook
  const { teamActivityPost: apiPost, loading: singlePostLoading, error: singlePostError } = useTeamActivityPost(actualId);
  
  // Also fetch all posts as fallback (for index-based lookup)
  const { teamActivityPosts, loading: allPostsLoading, error: allPostsError } = useTeamActivity(1, 1);
  
  // Determine which activity post to use
  let activityPost =  apiPost;
  
  // If no post from API and we have all posts, try index-based lookup as fallback
  if (!activityPost && teamActivityPosts && teamActivityPosts.length > 0) {
    const numericId = parseInt(id, 10);
    if (!isNaN(numericId)) {
      // If ID is numeric, try index-based lookup (1-based indexing)
      activityPost = teamActivityPosts[numericId - 1];
    }
  }
  
  const loading = singlePostLoading || (allPostsLoading && !apiPost);
  const error = singlePostError || (allPostsError && !apiPost);

  // Loading state
  if (loading && !activityPost) {
    return (
      <Container maxW="7xl" py={8}>
        <Button leftIcon={<ArrowLeftIcon />} onClick={() => navigate(-1)} mb={4}>
          Back
        </Button>
        <Spinner size="xl" color="#000B8C" />
      </Container>
    );
  }

  // Error state
  if (error && !activityPost) {
    return (
      <Container maxW="7xl" py={8}>
        <Button leftIcon={<ArrowLeftIcon />} onClick={() => navigate(-1)} mb={4}>
          Back
        </Button>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  // Activity post not found
  if (!activityPost) {
    return (
      <Container maxW="7xl" py={8}>
        <Button leftIcon={<ArrowLeftIcon />} onClick={() => navigate(-1)} mb={4}>
          Back
        </Button>
        Team activity post not found.
      </Container>
    );
  }

  return (
    <>
      <Container maxW="7xl" py={{ base: 4, md: 8 }} pb={{ base: 2, md: 4 }}>
        <TeamActivityBreadcrumb 
          title={String(activityPost.title || "Team Activity")} 
          fromWhere={"team_activity"} 
          currentPage={currentPage}
        />
        <TeamActivityHeroImage 
          image={activityPost.image} 
          title={String(activityPost.title || "Team Activity")} 
          isVideo={activityPost.isVideo}
          videoUrl={activityPost.videoUrl}
        />
        <TeamActivityTitleSection 
          tag={String(activityPost.tag || "Team Activity")} 
          title={String(activityPost.title || "Team Activity")} 
          blogPost={activityPost}
          author={typeof activityPost.author === 'string' ? activityPost.author : (activityPost.author?.fullName || activityPost.author?.name || "BA Systems Team")} 
          timeAgo={String(activityPost.timeAgo || "Recently")} 
        />
        <Divider mb={6} />
        <TeamActivityContent 
          desc={String(
            activityPost.post_desc || 
            activityPost.content || 
            activityPost.desc || 
            activityPost.summary || 
            ""
          )} 
          hashtags={Array.isArray(activityPost.hashtags) ? activityPost.hashtags : []} 
        />
      </Container>
      
      <CareersFooter />
    </>
  );
};

export default TeamActivityDetails; 